import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { jwtDecode } from 'jwt-decode';
import { authAPI } from '../services/api';

const AuthContext = createContext({});
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('token')); // only staff token
  const navigate = useNavigate();

  const isTokenValid = (token) => {
    if (!token) return false;
    try {
      const decoded = jwtDecode(token);
      return decoded.exp > Date.now() / 1000;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    const loadUser = async () => {
      const storedToken = localStorage.getItem('token');

      if (!storedToken || !isTokenValid(storedToken)) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setToken(null);
        setLoading(false);
        return;
      }

      try {
        const decoded = jwtDecode(storedToken);
        
        // If token belongs to a student, clear it – students are handled by StudentAuthContext
        if (decoded.role === 'student') {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setToken(null);
          setLoading(false);
          return;
        }

        // Try to fetch full user profile
        const response = await authAPI.getMe();
        setUser(response.data);
      } catch (error) {
        console.error('Failed to fetch staff user:', error);
        // Fallback to token data
        const decoded = jwtDecode(storedToken);
        setUser({
          email: decoded.email || '',
          name: decoded.name || '',
          role: decoded.role || 'user',
          isStudent: false
        });
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [token]);

  const login = async (email, password, regCode) => {
    try {
      const response = await authAPI.login({ email, password, reg_code: regCode });
      const { access_token, role, name, email: userEmail } = response.data;

      // Staff only – role must not be 'student'
      if (role === 'student') {
        toast.error('Please use the student login page');
        return { success: false };
      }

      const userData = {
        email: userEmail,
        name,
        role,
        isStudent: false
      };

      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      setToken(access_token);
      setUser(userData);

      toast.success('Login successful!');
      
      const redirectMap = {
        'lead_one': '/admin/dashboard',
        'lead_two': '/form-master/dashboard',
        'lead_four': '/teacher/dashboard'
      };
      navigate(redirectMap[role] || '/admin/dashboard');

      return { success: true };
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
      return { success: false, error: error.response?.data?.detail };
    }
  };

  const register = async (name, email, password, role, regCode) => {
    try {
      const response = await authAPI.register({
        name,
        email,
        password,
        role,
        reg_code: regCode
      });
      const data = response.data;
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user', JSON.stringify({
        email: data.email,
        name: data.name,
        role: data.role,
        user_id: data.user_id
      }));
      return { success: true, data };
    } catch (error) {
      const msg = error.response?.data?.detail || 'Registration failed';
      return { success: false, message: msg };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    navigate('/login');
    toast.success('Logged out successfully');
  };


  const googleLogin = async (token, regCode) => {
  try {
    const response = await authAPI.googleAuth({ token, reg_code: regCode });
    const { access_token, role, name, email } = response.data;

    const userData = { email, name, role, isStudent: false };
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(access_token);
    setUser(userData);

    toast.success('Login successful!');
    const redirectMap = {
      'lead_one': '/admin/dashboard',
      'lead_two': '/form-master/dashboard',
      'lead_four': '/teacher/dashboard'
    };
    navigate(redirectMap[role] || '/admin/dashboard');
    return { success: true };
  } catch (error) {
    toast.error(error.response?.data?.detail || 'Google login failed');
    return { success: false };
  }
};

const googleRegister = async (token, regCode, role) => {
  try {
    const response = await authAPI.googleAuth({ token, reg_code: regCode, role });
    const { access_token, role: userRole, name, email } = response.data;

    const userData = { email, name, role: userRole, isStudent: false };
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(access_token);
    setUser(userData);

    toast.success('Registration successful!');
    navigate('/admin/dashboard'); // or based on role if you prefer
    return { success: true };
  } catch (error) {
    toast.error(error.response?.data?.detail || 'Google registration failed');
    return { success: false };
  }
};

  const getToken = () => localStorage.getItem('token');

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    register,
    googleLogin,
    googleRegister,
    getToken,
    isAuthenticated: !!user && !!token && isTokenValid(token)
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

